import { createContext, useContext, type ReactNode } from 'react';
import { getFeatures } from './getFeatures.js';
import type { Feature, FeaturesConfig } from './getFeatures.js';

export type { Feature, FeaturesConfig };

type FeaturesContextType = {
  config: FeaturesConfig;
  locale: string;
};

const FeaturesContext = createContext<FeaturesContextType>({
  config: {},
  locale: '',
});

type FeaturesProviderProps<T extends string = string> = {
  value: { config: FeaturesConfig<T>; locale: string };
  children: ReactNode;
};

export function FeaturesProvider<T extends string = string>({
  value,
  children,
}: FeaturesProviderProps<T>) {
  return (
    <FeaturesContext.Provider value={value as FeaturesContextType}>
      {children}
    </FeaturesContext.Provider>
  );
}

/**
 * Returns evaluated feature flags for the current locale.
 * Pass your app's feature map as the type parameter for typed keys.
 * @example
 * const features = useFeatures<'emailSubscription' | 'hasCustomerAccount'>();
 * features.emailSubscription // boolean
 */
export function useFeatures<T extends string = string>(): Record<T, boolean> {
  const { config, locale } = useContext(FeaturesContext);
  return getFeatures(config as FeaturesConfig<T>, locale);
}
